import { TimezoneDropdownInput } from "#src/batteries-included-components/Dropdowns";
import {
  getPermissionDisplayLabel,
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Banner,
  CheckboxInput,
  DropdownInput,
  Icon,
  Panel,
  RadioInput,
  TelephoneInput,
  TextInput,
  Tooltip,
  useFormContext,
} from "@validereinc/common-components";
import {
  Resources,
  RolesAdapter,
  UserStatus,
  UserTypeEnum,
  UsersAdapter,
} from "@validereinc/domain";
import { FeatureFlags, Insights } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React, { useEffect, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { z } from "zod";
import styles from "./CreateUserDetailsStep.module.scss";

const { getNumberOfUsersThatCanAccessQuicksightConsole } = Insights.logic;

const cx = classNames.bind(styles);

const RolesFieldSchema = z.object({
  roles: z.array(z.string()).optional(),
});

const HumanCreateFormSchema = z
  .object({
    type: z.literal(UserTypeEnum.human),
    name: z.string(),
    email: z.string().email(),
    phone: z.string().nullable().optional(),
    timezone: z.string(),
    quicksight: z.boolean().optional(),
    can_access_quicksight_dashboards: z.boolean().optional(),
    can_access_quicksight_console: z.boolean().optional(),
  })
  .merge(RolesFieldSchema);

const MachineCreateFormSchema = z
  .object({
    type: z.literal(UserTypeEnum.machine),
    name: z.string(),
    timezone: z.string(),
    status: z.enum(["active", "disabled"]).optional(),
  })
  .merge(RolesFieldSchema);

export const UserCreateFormSchema = z.discriminatedUnion("type", [
  HumanCreateFormSchema,
  MachineCreateFormSchema,
]);
export type UserCreateFormType = z.infer<typeof UserCreateFormSchema>;

export const defaultHumanValues: UserCreateFormType = {
  type: UserTypeEnum.human,
  name: "",
  email: "",
  phone: null,
  timezone: "America/Toronto",
  quicksight: false,
  can_access_quicksight_dashboards: false,
  can_access_quicksight_console: false,
  roles: [],
};

export const defaultMachineValues: UserCreateFormType = {
  type: UserTypeEnum.machine,
  name: "",
  timezone: "America/Toronto",
  status: UserStatus.active,
  roles: [],
};

export const CreateUserDetailsStep = () => {
  const {
    v2: {
      companyInfo: { company },
    },
  } = useAuthenticatedContext();
  const { watch, reset } = useFormContext<UserCreateFormType>();
  const userType = watch("type");
  const isHuman = userType === UserTypeEnum.human;
  const selectedRoles = watch("roles");
  const [enteredEmail] = useDebounce(watch("email"), 500);

  const [isManageInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
  });

  const duplicateQueryPayload: Parameters<typeof UsersAdapter.getList>[0] = {
    filters: {
      email: enteredEmail,
      isAlreadyFormatted: true,
    } as Parameters<typeof UsersAdapter.getList>[0]["filters"],
  };

  const { data: duplicateUsers } = useQuery({
    queryKey: ["users", duplicateQueryPayload],
    queryFn: () => UsersAdapter.getList(duplicateQueryPayload),
    enabled: isHuman && Boolean(enteredEmail),
  });

  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: [Resources.USER],
    queryFn: async () => {
      return UsersAdapter.getList({});
    },
  });

  const rolesPermissionsQueries = useQueries({
    queries:
      selectedRoles?.map((roleId) => ({
        queryKey: ["roles", roleId, "permissions"],
        queryFn: () => RolesAdapter.permissions.getMany({ id: roleId }),
        staleTime: 3 * 60 * 1000,
      })) ?? [],
  });
  const rolesToAssociatedPermissionsList = useMemo(() => {
    if (rolesPermissionsQueries.some((q) => q.isLoading)) {
      return [];
    }

    return Array.from(
      rolesPermissionsQueries
        .reduce<Set<string>>((set, query) => {
          if (!query.data?.length) {
            return set;
          }

          query.data.forEach((perm) =>
            set.add(getPermissionDisplayLabel(perm.name))
          );

          return set;
        }, new Set())
        .values()
    );
  }, [rolesPermissionsQueries]);

  useEffect(() => {
    if (userType === UserTypeEnum.machine) {
      reset(defaultMachineValues);
    } else {
      reset(defaultHumanValues);
    }
  }, [userType]);

  const numberOfUsersThatCanAccessQuicksightConsole =
    getNumberOfUsersThatCanAccessQuicksightConsole(usersData?.data ?? []);

  const isCompanyQuicksightConsoleSeatLimitReached =
    numberOfUsersThatCanAccessQuicksightConsole >=
    (company?.quicksight_console_seat_limit ?? 0);

  const isLoaded = !isUsersLoading;

  return (
    <>
      <Panel
        title="Details"
        loaded={isLoaded}
      >
        <div className={cx("detailsStepContainer")}>
          <RadioInput
            name="type"
            label="User type"
            options={[
              { value: UserTypeEnum.human, label: "Human" },
              { value: UserTypeEnum.machine, label: "Machine" },
            ]}
            labelKey="label"
            valueKey="value"
            isRequired
          />

          <TextInput
            name="name"
            label="Name"
            type="text"
            isRequired
          />

          {isHuman && (
            <>
              <TextInput
                name="email"
                label="Email"
                type="text"
                rules={{
                  validate: {
                    validateEmail: (email: string) =>
                      typeof email === "string" && email.length > 0
                        ? HumanCreateFormSchema.shape.email.safeParse(email)
                            .success
                          ? true
                          : "Email is invalid"
                        : true,
                    uniqueEmail: () => {
                      return duplicateUsers?.total_entries
                        ? "An account with this email already exists"
                        : true;
                    },
                  },
                }}
                isRequired
                description="Cannot be changed again"
              />

              <TelephoneInput
                name="phone"
                label="Phone Number"
              />
            </>
          )}

          <TimezoneDropdownInput name="timezone" />
        </div>
      </Panel>
      <Panel
        title="Roles & Permissions"
        loaded={isLoaded}
      >
        {isHuman && (
          <>
            <CheckboxInput
              name="quicksight"
              label="User has access to Insights Dashboards"
              isLabelShown={false}
              isFluid
            />
            {isManageInsightsAvailable && (
              <div className={cx("checkbox-with-tooltip-container")}>
                <CheckboxInput
                  componentClassName={cx(
                    "can-access-quicksight-console-checkbox"
                  )}
                  className={cx("can-access-quicksight-console-checkbox")}
                  name="can_access_quicksight_console"
                  label="User has access to Manage Insights"
                  isLabelShown={false}
                  isDisabled={isCompanyQuicksightConsoleSeatLimitReached}
                  isFluid
                />
                {isCompanyQuicksightConsoleSeatLimitReached && (
                  <Tooltip
                    content={`This company has reached its limit of ${company?.quicksight_console_seat_limit} users that can access Manage Insights`}
                    contentClassName={cx("tooltip-container")}
                  >
                    <Icon
                      className={cx("tooltip-icon")}
                      variant="info"
                      size={16}
                    />
                  </Tooltip>
                )}
              </div>
            )}
          </>
        )}
        <DropdownInput
          key="roles"
          name="roles"
          onFetchData={async (payload) => {
            let { data } = await RolesAdapter.getList({
              ...payload,
              filters: {
                name: payload.searchTerm,
                status: "active",
              },
            });

            // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
            if (Array.isArray(payload.value)) {
              data = data.filter((d) => payload.value.includes(d.id));
            }

            return data;
          }}
          labelKey="name"
          valueKey="id"
          label="Roles (active only)"
          placeholder="Assign Roles"
          isMulti
          isInline
        />
        {rolesToAssociatedPermissionsList.length ? (
          <Banner
            className={cx("associatedPermissionsBanner")}
            variant="info"
            titleText="The following permissions will be granted to this user based on the assigned roles:"
            description={rolesToAssociatedPermissionsList.join(", ")}
          />
        ) : (
          <Banner
            className={cx("associatedPermissionsBanner")}
            variant="warning"
            titleText="This user will not have access to anything in the application"
            description="If this is not intentional, assign a role to get started."
          />
        )}
      </Panel>
    </>
  );
};
